/**
 * Tell React Query when the app comes back to the foreground.
 *
 * React Query decides "is the window focused" through its `focusManager`, and on
 * the web it wires itself to `visibilitychange`. ON REACT NATIVE THERE IS NO
 * SUCH EVENT, and the library says so: without an explicit AppState listener the
 * focus state never changes, so `refetchOnWindowFocus` is inert. It has been
 * inert here — `submissions.ts` sets it to `true` and it has never once fired on
 * a device.
 *
 * WHAT THAT COSTS, observed rather than theorised. A strategic-analysis run died
 * at 21:55 and the screen still read "Checking every claim against its sources"
 * at 00:04 — two hours later. The run's effective state is derived from
 * `updated_at` against `Date.now()` AT RENDER TIME, so the ten-minute staleness
 * rule can only fire if something re-renders. Polling is what normally does
 * that, and a backgrounded app throttles its timers; coming back to the app then
 * refetched nothing, so the last state computed before the phone went to sleep
 * is what stayed on screen. The rule was correct and unreachable.
 *
 * Wired once, at the root, because it is a property of the app rather than of
 * any screen. Every query gets it, which is the point: a stale screen after a
 * lock is not a strategic-analysis problem.
 */

import { AppState, type AppStateStatus, Platform } from 'react-native';
import { focusManager } from '@tanstack/react-query';

/**
 * Start forwarding foreground/background transitions to React Query.
 *
 * Returns the unsubscribe, so a caller that owns a lifecycle can release it.
 * Web needs nothing: React Query already listens for `visibilitychange` there,
 * and adding a second source would only make the two disagree.
 */
export function startQueryFocusTracking(): () => void {
  if (Platform.OS === 'web') return () => {};

  const subscription = AppState.addEventListener('change', (status: AppStateStatus) => {
    // `active` ONLY. `inactive` is the iOS transitional state — the app switcher,
    // an incoming call, a permission sheet — and treating it as blurred would
    // mark queries stale for a state the user never left.
    focusManager.setFocused(status === 'active');
  });

  return () => subscription.remove();
}
